import { useEffect, useRef, useState } from 'react';

import type { UUID } from '@aline/common';
import { Apollo } from 'src/gql/generated';

export const ACTIVITY_LANE = {
  External: 'external',
  Team: 'team',
} as const;

export type ActivityLane = (typeof ACTIVITY_LANE)[keyof typeof ACTIVITY_LANE];

const LANE_AUDIENCE = {
  [ACTIVITY_LANE.External]: Apollo.ActivityAudience.External,
  [ACTIVITY_LANE.Team]: Apollo.ActivityAudience.Internal,
} as const satisfies Record<ActivityLane, Apollo.ActivityAudience>;

interface ActivityLaneState {
  /** The audience the feed reads and new comments post with; null reads the whole feed. */
  audience: Apollo.ActivityAudience | null;
  lane: ActivityLane;
  /** A deep link's lane is still being looked up, so the feed waits rather than anchoring wrong. */
  resolving: boolean;
  setLane: (lane: ActivityLane) => void;
  /** An internal viewer on a task with external assignees picks a lane; everyone else sees one feed. */
  showSwitch: boolean;
}

/**
 * Which lane of a task's activity is on screen. Team holds the owner org's own items and External
 * holds what external assignees see. The lane starts on Team for each task, and a deep link opens
 * the lane its item lives in.
 */
export function useActivityLane(options: {
  anchorId: UUID | null;
  isPreview: boolean;
  task: { hasExternalAssignees: boolean; id: UUID; requesterIsInternal: boolean };
  skip?: boolean;
}): ActivityLaneState {
  const { anchorId, isPreview, skip = false, task } = options;
  const showSwitch = task.requesterIsInternal && task.hasExternalAssignees && !isPreview;
  const [chosen, setChosen] = useState<{ lane: ActivityLane; taskId: UUID }>({
    lane: ACTIVITY_LANE.Team,
    taskId: task.id,
  });
  const lane = chosen.taskId === task.id ? chosen.lane : ACTIVITY_LANE.Team;
  const setLane = (next: ActivityLane) => setChosen({ lane: next, taskId: task.id });

  const lookupAnchor = showSwitch && !skip && Boolean(anchorId);
  const { data, loading } = Apollo.useGetTaskActivityQuery({
    fetchPolicy: 'network-only',
    skip: !lookupAnchor,
    variables: { anchor: anchorId, audience: null, limit: 1, taskId: task.id },
  });
  const anchorAudience =
    data?.task?.activity.rows.find(row => row.id === anchorId)?.audience ?? null;
  const handledAnchorRef = useRef<UUID | null>(null);
  useEffect(() => {
    if (!anchorId || !anchorAudience || handledAnchorRef.current === anchorId) return;
    handledAnchorRef.current = anchorId;
    setChosen({
      lane:
        anchorAudience === Apollo.ActivityAudience.Internal
          ? ACTIVITY_LANE.Team
          : ACTIVITY_LANE.External,
      taskId: task.id,
    });
  }, [anchorAudience, anchorId, task.id]);

  return {
    audience: getLaneAudience({ isPreview, lane, showSwitch }),
    lane,
    resolving:
      lookupAnchor && handledAnchorRef.current !== anchorId && (loading || Boolean(anchorAudience)),
    setLane,
    showSwitch,
  };
}

function getLaneAudience(options: {
  isPreview: boolean;
  lane: ActivityLane;
  showSwitch: boolean;
}): Apollo.ActivityAudience | null {
  if (options.isPreview) return Apollo.ActivityAudience.External;
  return options.showSwitch ? LANE_AUDIENCE[options.lane] : null;
}
